import { FARM_DIAGNOSTICS_UNCROPPED_LABEL } from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputStepType, type IInputStepSeed } from 'interfaces/IInputStep';
import { InputTaskType } from 'interfaces/IInputTask';
import { convertToKg } from 'utils/unitConversion';
import { resolveAreaHa, resolveEffectiveRate } from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import type { RotationGrid } from './computeRotationGrid';

/** One crop's share of the year's planted area. */
export interface EnterpriseSlice {
  cropName: string;
  areaHa: number;
  /** Fraction of the total, 0–1. */
  share: number;
}

/** Seed needed for one crop and variety, summed over every plan sowing it. */
export interface SeedRequirementLine {
  key: string;
  cropName: string;
  varietyName: string | null;
  /** Area sown with it (ha). */
  areaHa: number;
  quantityKg: number;
}

/** A plan left out of the seed totals because it has no sowing rate. */
export interface MissingSeedPlan {
  inputId: string;
  paddockName: string;
  cropName: string;
  areaHa: number | null;
}

export interface RotationSummary {
  enterprise: {
    slices: EnterpriseSlice[];
    totalHa: number;
  };
  seed: {
    lines: SeedRequirementLine[];
    missing: MissingSeedPlan[];
    /** Area of the plans left out (ha). */
    missingHa: number;
  };
}

const isValidId = (id: string | null | undefined): id is string =>
  typeof id === 'string' && id.length > 0;

/**
 * The Rotation planner's summary for one year (OP-2501), from the plans the
 * grid shows for it — so it follows the grid's scope and Plan / Actual
 * choice, and changes as soon as a cell does.
 *
 * - Enterprise mix: each plan's own area by its crop, largest first.
 * - Seed requirements: for each Seed step in the shown category's crop
 *   establishment tasks, rate × area as Live totals' Quantity required works
 *   it out (resolveEffectiveRate × resolveAreaHa, in kg), grouped by the
 *   crop and variety being sown — so a mix counts each seed it contains.
 * - A plan with no Seed step with a rate is listed as missing, so the total
 *   is never quietly short.
 */
export const computeRotationSummary = (
  grid: RotationGrid,
  season: number,
  inputsById: Map<string, IInput>,
  gUnits: GUnit[]
): RotationSummary => {
  const enterprise = new Map<string, number>();
  const seed = new Map<string, SeedRequirementLine>();
  const missing: MissingSeedPlan[] = [];

  grid.farms.forEach((farm) =>
    farm.paddocks.forEach((paddock) =>
      (paddock.cells[season] ?? []).forEach((cell) => {
        const cropName = cell.cropName ?? FARM_DIAGNOSTICS_UNCROPPED_LABEL;
        if (cell.areaHa) {
          enterprise.set(cropName, (enterprise.get(cropName) ?? 0) + cell.areaHa);
        }

        const input = inputsById.get(cell.inputId);
        let sown = false;
        (input?.tasks ?? []).forEach((task) => {
          if (
            task.taskType !== InputTaskType.CropEstablishment ||
            task.category !== cell.shownCategory
          ) {
            return;
          }
          task.steps.forEach((step) => {
            if (step.stepType !== InputStepType.Seed || !input) return;
            const seedStep = step as IInputStepSeed;
            const rate = resolveEffectiveRate(step, task, gUnits);
            const areaHa = resolveAreaHa(step, task, input, gUnits);
            if (!rate || rate <= 0 || areaHa <= 0 || !seedStep.appUnit) return;
            const quantityKg = convertToKg(rate * areaHa, seedStep.appUnit, gUnits);
            if (quantityKg == null || quantityKg <= 0) return;

            sown = true;
            const crop = seedStep.item || cropName;
            const variety = seedStep.item2 || null;
            const key = [
              isValidId(seedStep.globalId) ? seedStep.globalId : crop,
              isValidId(seedStep.globalId2) ? seedStep.globalId2 : (variety ?? ''),
            ].join('|');
            const line = seed.get(key);
            if (line) {
              line.areaHa += areaHa;
              line.quantityKg += quantityKg;
            } else {
              seed.set(key, {
                key,
                cropName: crop,
                varietyName: variety,
                areaHa,
                quantityKg,
              });
            }
          });
        });

        if (!sown) {
          missing.push({
            inputId: cell.inputId,
            paddockName: paddock.paddockName,
            cropName,
            areaHa: cell.areaHa,
          });
        }
      })
    )
  );

  const totalHa = Array.from(enterprise.values()).reduce((a, b) => a + b, 0);
  return {
    enterprise: {
      totalHa,
      slices: Array.from(enterprise, ([name, areaHa]) => ({
        cropName: name,
        areaHa,
        share: totalHa > 0 ? areaHa / totalHa : 0,
      })).sort((a, b) => b.areaHa - a.areaHa),
    },
    seed: {
      lines: Array.from(seed.values()).sort(
        (a, b) =>
          a.cropName.localeCompare(b.cropName) ||
          (a.varietyName ?? '').localeCompare(b.varietyName ?? '')
      ),
      missing,
      missingHa: missing.reduce((total, plan) => total + (plan.areaHa ?? 0), 0),
    },
  };
};
